<div class="modal-header align-items-start">
  <div class="d-flex flex-column">
    <h4 class="modal-title mb-1">
      {{ modalStep === 'search' ? ('companyProfile.addMember.title' | translate) : ('companyProfile.invite.title' | translate) }}
    </h4>
    @if (modalStep === 'form') {
      <p class="text-muted small mb-0">{{ 'userProfile.edit.requiredFieldsPrefix' | translate }} <span class="text-danger">*</span> {{ 'userProfile.edit.requiredFieldsSuffix' | translate }}</p>
    }
  </div>
  <button type="button" class="btn-close" aria-label="Close" (click)="activeModal.dismiss()"></button>
</div>

<div class="modal-body">
  <!-- Error Alert -->
  @if (inviteError) {
    <div class="alert alert-danger d-flex align-items-center mb-5">
      <i class="fa fa-exclamation-circle me-2"></i>
      <span>{{ inviteError }}</span>
    </div>
  }

  <!-- Step 1: Search -->
  @if (modalStep === 'search') {
    <p class="text-muted mb-4">{{ 'companyProfile.addMember.searchUser' | translate }}</p>

    <!-- Search Input -->
    <div class="mb-5">
      <div class="input-group input-group-lg">
        <span class="input-group-text bg-light border-end-0">
          <i class="fa fa-search text-muted"></i>
        </span>
        <input
          type="text"
          class="form-control border-start-0 ps-0"
          [placeholder]="'companyProfile.addMember.searchPlaceholder' | translate"
          [value]="searchQuery"
          (input)="onSearchInput($any($event.target).value)"
          autofocus
        />
        @if (searchLoading) {
          <span class="input-group-text bg-light">
            <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
          </span>
        }
      </div>
    </div>

    <!-- Selected User -->
    @if (selectedUser) {
      <div class="selected-user-card mb-5">
        <div class="d-flex align-items-center">
          <div class="symbol symbol-50px me-4">
            <div class="symbol-label bg-light-success text-success fw-bold fs-4">
              {{ getInitials(selectedUser.first_name, selectedUser.last_name) }}
            </div>
          </div>
          <div class="flex-grow-1">
            <div class="fw-bold fs-5">{{ selectedUser.first_name }} {{ selectedUser.last_name }}</div>
            <div class="text-muted fs-7">{{ selectedUser.email }}</div>
          </div>
          <button class="btn btn-sm btn-icon btn-light" (click)="deselectUser()">
            <i class="fa fa-times"></i>
          </button>
        </div>

        <!-- Role Selection for Selected User -->
        <div class="mt-4">
          <label class="form-label">{{ 'companyProfile.invite.role' | translate }}</label>
          <div class="row g-3">
            @for (role of roleOptions; track role.role_id) {
              <div class="col-6">
                <div
                  class="role-card"
                  [class.selected]="inviteRoleId === role.role_id"
                  (click)="inviteRoleId = role.role_id"
                >
                  <div class="role-icon">
                    @if (role.role_id === 1) {
                      <i class="fa fa-shield-halved text-primary"></i>
                    } @else {
                      <i class="fa fa-user text-secondary"></i>
                    }
                  </div>
                  <div class="role-name">{{ role.role_name }}</div>
                  <div class="role-desc">{{ role.description }}</div>
                </div>
              </div>
            }
          </div>
        </div>
      </div>
    }

    <!-- Search Results -->
    @if (!selectedUser && searchQuery.length >= 2) {
      <div class="search-results mb-5">
        @if (searchResults.length > 0) {
          <div class="search-results-list">
            @for (user of searchResults; track user.user_id) {
              <div class="search-result-item" (click)="selectUser(user)">
                <div class="symbol symbol-40px me-3">
                  <div class="symbol-label bg-light-primary text-primary fw-bold">
                    {{ getInitials(user.first_name, user.last_name) }}
                  </div>
                </div>
                <div class="flex-grow-1">
                  <div class="fw-semibold">{{ user.first_name }} {{ user.last_name }}</div>
                  <div class="text-muted fs-7">{{ user.email }}</div>
                </div>
                <i class="fa fa-chevron-right text-muted"></i>
              </div>
            }
          </div>
        } @else if (!searchLoading) {
          <div class="no-results text-center py-8">
            <i class="fa fa-user-slash fs-3x text-muted mb-4 d-block"></i>
            <p class="text-muted mb-0">{{ 'companyProfile.addMember.noResults' | translate }}</p>
          </div>
        }
      </div>
    }

    <!-- Create New User Option -->
    @if (!selectedUser) {
      <div class="create-new-section">
        <div class="separator my-5">
          <span class="text-muted fs-7">OR</span>
        </div>
        <div class="text-center">
          <p class="text-muted mb-3">{{ 'companyProfile.addMember.noResults' | translate }}</p>
          <app-button
            [variant]="'outline'"
            [label]="'companyProfile.invite.title' | translate"
            [iconClass]="'fa fa-envelope'"
            (clicked)="showCreateForm()"
          />
        </div>
      </div>
    }
  }

  <!-- Step 2: Create New User Form -->
  @if (modalStep === 'form') {
    <!-- Back Button -->
    <div class="mb-5">
      <button class="btn btn-sm btn-light" (click)="backToSearch()">
        <i class="fa fa-arrow-left me-2"></i> {{ 'common.back' | translate }}
      </button>
    </div>

    <div class="row spacing-15">
      <!-- First Name -->
      <div class="col-md-6">
        <app-input-component
          [label]="('userProfile.edit.firstName' | translate) + ' *'"
          [type]="'text'"
          [(value)]="inviteFirstName"
          [placeholder]="'userProfile.edit.firstName' | translate"
          [invalid]="isFirstNameInvalid"
        />
      </div>

      <!-- Last Name -->
      <div class="col-md-6">
        <app-input-component
          [label]="('userProfile.edit.lastName' | translate) + ' *'"
          [type]="'text'"
          [(value)]="inviteLastName"
          [placeholder]="'userProfile.edit.lastName' | translate"
          [invalid]="isLastNameInvalid"
        />
      </div>
    </div>

    <div class="row spacing-15">
      <!-- Email -->
      <div class="col-md-6">
        <app-input-component
          [label]="('companyProfile.invite.email' | translate) + ' *'"
          [type]="'email'"
          [(value)]="inviteEmail"
          [placeholder]="'companyProfile.invite.emailPlaceholder' | translate"
          [invalid]="isEmailInvalid"
        />
      </div>

      <!-- Phone -->
      <div class="col-md-6">
        <app-input-component
          [label]="'companyProfile.edit.phone' | translate"
          [type]="'text'"
          [(value)]="invitePhone"
          [placeholder]="'companyProfile.edit.phone' | translate"
        />
      </div>
    </div>

    <!-- Role Selection -->
    <div class="row spacing-15">
      <div class="col-12">
        <label class="form-label required">{{ 'companyProfile.invite.role' | translate }}</label>
        <div class="row g-3">
          @for (role of roleOptions; track role.role_id) {
            <div class="col-md-6">
              <div
                class="role-card"
                [class.selected]="inviteRoleId === role.role_id"
                (click)="inviteRoleId = role.role_id"
              >
                <div class="role-icon">
                  @if (role.role_id === 1) {
                    <i class="fa fa-shield-halved text-primary"></i>
                  } @else {
                    <i class="fa fa-user text-secondary"></i>
                  }
                </div>
                <div class="role-name">{{ role.role_name }}</div>
                <div class="role-desc">{{ role.description }}</div>
              </div>
            </div>
          }
        </div>
      </div>
    </div>
  }
</div>

<div class="modal-footer">
  @if (modalStep === 'search' && selectedUser) {
    <app-button
      [variant]="'outline'"
      [label]="'companyProfile.addMember.cancel' | translate"
      (clicked)="activeModal.dismiss()"
    />
    <app-button
      [variant]="'primary'"
      [label]="inviteLoading ? ('companyProfile.addMember.adding' | translate) : ('companyProfile.addMember.add' | translate)"
      [iconClass]="inviteLoading ? 'fa fa-spinner fa-spin' : 'fa fa-plus'"
      [disabled]="inviteLoading"
      (clicked)="addSelectedUser()"
    />
  } @else if (modalStep === 'form') {
    <app-button
      [variant]="'outline'"
      [label]="'companyProfile.invite.cancel' | translate"
      (clicked)="activeModal.dismiss()"
    />
    <app-button
      [variant]="'primary'"
      [label]="inviteLoading ? ('companyProfile.invite.sending' | translate) : ('companyProfile.invite.send' | translate)"
      [iconClass]="inviteLoading ? 'fa fa-spinner fa-spin' : 'fa fa-paper-plane'"
      [disabled]="inviteLoading"
      (clicked)="submitInvite()"
    />
  } @else {
    <app-button
      [variant]="'outline'"
      [label]="'common.close' | translate"
      (clicked)="activeModal.dismiss()"
    />
  }
</div>
